import fetchStripeCustomerPortal from '@/chat-common/fetch/fetch-customer-portal'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { AuthContext } from '@/context/auth-context'
import { SubscriptionStatus } from '@/firebase/auth/auth-jwt-schema'
import { StripeSubscriptionInterval } from '@/account/store/schemas'
import { setStripePortalDialogVisible } from '@/store/slices/ui-state.slice'
import { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { OpenNew, WarningTriangle } from '@/assets/icons'
import { useContext, useEffect, useState } from 'react'
import createStripeCustomer from '@/chat-common/fetch/fetch-create-customer'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { formatCentsToDollarsCompact } from '@/util/money'
import { AccountActions } from '../store/slice'
import { selectDisplayCostPerSeatInCents, selectHasForeverPercentDiscount } from '../store/selectors'
import clsx from 'clsx'
import { Modal, ModalContent } from '@/components/modal/Modal'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'

/**
 * The StripePortalDialog handles fetching and displaying of the Stripe portal Link.
 * This is to decouple the logic from the buttons that may activate this UI.
 */
export default function StripePortalDialog() {
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()

  // Auth context
  const { userAccountData } = useContext(AuthContext)
  const canViewSubscriptionInfo = userAccountData?.canViewStripeSubscriptionInfo
  const isOrgSubscription = userAccountData?.orgData?.id != null
  const isInTrial = userAccountData?.subscriptionStatus === SubscriptionStatus.TRIAL

  // Local state
  const [isNonSubscribedOrgAdmin, setIsNonSubscribedOrgAdmin] = useState(false)
  const [portalUrl, setPortalUrl] = useState<string | null>(null)
  const [portalUrlLoading, setPortalUrlLoading] = useState<boolean>(false)
  const [portalUrlError, setPortalUrlError] = useState<string | null>(null)

  // Portal url variables to help with Open Payment Portal button states
  const isPortalUrlResolved = portalUrl !== null || portalUrlError !== null
  const isPreparingPortal = portalUrlLoading || !isPortalUrlResolved
  const canOpenPortal = !!portalUrl && !portalUrlLoading && !portalUrlError

  // Redux state
  const visible = useAppSelector((state: RootState) => state.uiState.stripePortalDialogVisible)

  const isLoading = useAppSelector((state: RootState) => state.accountState.loading)
  const isInitialLoadComplete = useAppSelector((state: RootState) => state.accountState.initialLoadComplete)
  const subscriptionInterval = useAppSelector((state: RootState) => state.accountState.stripeSubscriptionInterval)
  const subscriptionUnitPrice = useAppSelector((state: RootState) => state.accountState.costPerSeatInCents)
  const discountPercent = useAppSelector((state: RootState) => state.accountState.appliedCoupon?.percent_off)
  const hasDiscount = useAppSelector((state: RootState) => state.accountState.hasDiscount)
  const hasForeverPercentDiscount = useAppSelector(selectHasForeverPercentDiscount)
  const displayCostPerSeatInCents = useAppSelector(selectDisplayCostPerSeatInCents)
  const hasLegacyStudentPlan = useAppSelector((state: RootState) => state.accountState.hasLegacyStudentPlan)

  // Only show the loader during initial fetch. For automatic background refreshes of the account data,
  // we avoid showing the loader to prevent unexpected loading states when data is already visible.
  const subscriptionInfoLoading = isLoading && !isInitialLoadComplete

  // Data required to display subscription billing info
  const isSubscriptionBillingInfoAvailable = subscriptionUnitPrice !== null && displayCostPerSeatInCents !== null && subscriptionInterval !== null
  const showDisclaimer = hasDiscount && !hasForeverPercentDiscount

  const priceDescriptor = `${isOrgSubscription ? 'per seat' : ''} / ${subscriptionInterval}`

  const priceText = displayCostPerSeatInCents != null ? `${formatCentsToDollarsCompact(displayCostPerSeatInCents)} ${priceDescriptor}` : ''
  const originalPriceText = subscriptionUnitPrice != null ? `${formatCentsToDollarsCompact(subscriptionUnitPrice)} ${priceDescriptor}` : ''

  /**
   * Handles the open payment portal button click.
   * Closes the modal and tracks the event.
   */
  const handleOpenPaymentPortal = () => {
    if (!portalUrl || portalUrlLoading) return

    // Close the dialog and track the event
    dispatch(setStripePortalDialogVisible(false))
    trackEvent(AnalyticsEvent.StripePortalOpened)

    // Open the portal in a new tab
    const win = window.open(portalUrl, '_blank', 'noopener,noreferrer')
    if (win) win.opener = null
  }

  /**
   * Handles the closing of the stripe portal modal.
   */
  const handleCloseStripePortalDialog = () => {
    dispatch(setStripePortalDialogVisible(false))
  }

  useEffect(() => {
    if (canViewSubscriptionInfo) {
      dispatch(AccountActions.refreshPaymentDetails({ forceRefresh: true }))
    }
  }, [canViewSubscriptionInfo, dispatch])

  // Get the portal url
  async function requestPortalUrl() {
    try {
      setPortalUrlError(null)
      setPortalUrlLoading(true)
      let portalUrl

      if (userAccountData?.subscriptionStatus == SubscriptionStatus.PRE_TRIAL) {
        portalUrl = await createStripeCustomer()
      } else {
        const portalResponse = await fetchStripeCustomerPortal()
        const { url, isNonSubscribedOrg } = await portalResponse
        portalUrl = url

        setIsNonSubscribedOrgAdmin(isNonSubscribedOrg)
      }

      if (portalUrl != undefined && (portalUrl as unknown as { detail?: string }).detail === 'Customer already exists') {
        const portalResponse = await fetchStripeCustomerPortal()
        const { url } = await portalResponse
        portalUrl = url
      }

      if (portalUrl == undefined || portalUrl == null) {
        console.error('We are still completing your registration. Please refresh the page and try again shortly.')
        setPortalUrlError('We are still completing your registration. Please refresh the page and try again shortly.')
      } else {
        setPortalUrl(portalUrl)
      }
    } catch (error) {
      console.error(error)
      setPortalUrlError('There was an error loading your account portal link. Please try again.')
    } finally {
      setPortalUrlLoading(false)
    }
  }

  // Fetch the portal url on visible
  useEffect(() => {
    if (visible) {
      requestPortalUrl()
    }
  }, [visible])

  return (
    <Modal open={visible} onOpenChange={(open) => !open && handleCloseStripePortalDialog()}>
      <ModalContent
        stacked={true} // Raise above other open modals
        title="Manage Your Subscription"
        width="md"
        height="max-full"
        footer={
          isNonSubscribedOrgAdmin ? (
            <ModalFooterWithActions secondaryAction={{ label: 'Cancel', onClick: handleCloseStripePortalDialog }} />
          ) : (
            <ModalFooterWithActions
              secondaryAction={{ label: 'Cancel', onClick: handleCloseStripePortalDialog }}
              primaryAction={{
                label: isPreparingPortal ? 'Preparing Portal...' : 'Open Payment Portal',
                onClick: handleOpenPaymentPortal,
                disabled: !canOpenPortal,
                loading: isPreparingPortal,
                trailingIcon: <OpenNew className="w-4 h-4" />,
              }}
              inlineMessage={portalUrlError ? { variant: 'error', children: portalUrlError } : undefined}
            />
          )
        }
      >
        {isNonSubscribedOrgAdmin ? (
          // Non-subscribed org admin message
          <p className="text-brand-neutral-600">
            We are updating the billing portal. You will soon gain access to this page. Please contact your organization administrator if you have any
            questions.
          </p>
        ) : (
          // Main content with subscription info and open payment portal button
          <div className="space-y-3.5">
            {canViewSubscriptionInfo && (
              <>
                {/* Loader while fetching subscription info */}
                {subscriptionInfoLoading && (
                  <div className="flex items-center justify-center my-8">
                    <CircularProgressContinuousSized size={20} thickness={7} />
                  </div>
                )}

                {/* Subscription Price */}
                {!subscriptionInfoLoading && isSubscriptionBillingInfoAvailable && (
                  <div className="font-medium">
                    {/* Subscription type */}
                    <div>
                      {`${!hasLegacyStudentPlan ? 'Standard ' : ''}`}
                      {`${subscriptionInterval == StripeSubscriptionInterval.MONTH ? 'Monthly' : 'Annual'}`} Subscription
                    </div>

                    {/* Show striked-through original price if there is a forever percent discount */}
                    {hasForeverPercentDiscount && discountPercent != null && (
                      <div className="flex gap-2 mt-3.5 items-center mb-1 text-brand-neutral-600 text-sm">
                        <p className="line-through">{originalPriceText}</p>
                        <div className="px-1 py-[1px] rounded text-xs bg-brand-neutral-200 border border-brand-neutral-300">{discountPercent}% OFF</div>
                      </div>
                    )}

                    {/* Pricing display - styling differs slightly based on whether the original price is shown above */}
                    <p className={clsx('text-brand-neutral-600', hasForeverPercentDiscount && discountPercent != null ? 'text-sm' : 'font-normal')}>
                      {priceText}
                      {hasLegacyStudentPlan && ' (student plan)'}
                      {showDisclaimer && (
                        <span>
                          {` - `}
                          <span className="text-brand-800 text-sm">Price shown before promotion</span>
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </>
            )}

            <p>Continue to our secure payment portal to manage your subscription.</p>

            {/* If the user has a standard subscription and is in a trial, show a warning that updating their billing frequency will end their trial */}
            {isInTrial && !hasLegacyStudentPlan && (
              <div className="bg-yellow-100 p-2 gap-1.5 flex items-start text-yellow-800">
                <WarningTriangle className="w-[18px] h-[18px] shrink-0" />
                <p className="text-sm">Updating your billing frequency now will end your free trial early and process payment today</p>
              </div>
            )}
          </div>
        )}
      </ModalContent>
    </Modal>
  )
}
